/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { capitalize } from 'Utility/string';
import { diagnoses, diagnosesByPlatform } from '../constants/diagnoses';
import { formatStringArray } from './queryFormatters';
import { IExtendedSearch } from '../..';

export const maxDateToNextYearOrMonth = (date: PartialDate): PartialDate => {
  // Add one more month or year to date so that it covers the selected month or year
  if (date.every(d => d)) return date; // Nothing to do
  if (date[0] && date[1]) {
    // Add month
    if (date[1] === 12) return [date[0] + 1, 1, date[2]];
    return [date[0], date[1] + 1, date[2]];
  }
  return [date[0] + 1, date[1], date[2]]; // Add year
};

export const getDiagnosisQueryString = (platform?: Platform, includedDiagnoses?: string[] | 'noCropping') => {
  let query = '';
  if (Array.isArray(includedDiagnoses) && includedDiagnoses.length > 0) {
    // If search is limited to a specific diagnosis or a set of diagnoses
    query += formatStringArray(
      Array.isArray(includedDiagnoses)
        ? (includedDiagnoses
            .flatMap<string | undefined>((d: string) => diagnoses[d])
            .filter(s => Boolean(s)) as string[])
        : [],
    );
  } else if (platform) {
    const dgs = diagnosesByPlatform[platform];
    if (Array.isArray(dgs) && dgs.length > 0) {
      // Include all diagnoses for the platform
      query += formatStringArray(dgs);
    }
  }
  return query;
};

export const getMinMaxFieldParent = (field: string): string | undefined => {
  const splitter = field.includes('min') ? 'min' : 'max';
  const fieldSplit = field.split(splitter)[1];
  if (!fieldSplit) return;
  return fieldSplit.charAt(0).toLowerCase() + fieldSplit.slice(1);
};

export const getMinMaxFieldNames = (name: string): [string, string] => {
  const [minDateField, maxDateField] = ['min', 'max'].map(minOrMax => `${minOrMax}${capitalize(name)}`);
  return [minDateField!, maxDateField!];
};

/** Function to help with updating min-max number field values of extended search */
export const updateExtendedSearchMinMax = (
  searchFields: IExtendedSearch['searchFields'],
  parentField: keyof IExtendedSearch['searchFields'],
  value: unknown,
  fieldToUpdate: string,
  fieldSuffix: string,
): { [key: string]: number | undefined } | 'noCropping' => {
  const parentFieldValue = searchFields[parentField];
  const minValue = parentFieldValue?.[`min${fieldSuffix}` as keyof typeof parentFieldValue];
  const maxValue = parentFieldValue?.[`max${fieldSuffix}` as keyof typeof parentFieldValue];
  switch (fieldToUpdate) {
    case `min${fieldSuffix}`:
      return {
        [`min${fieldSuffix}`]: typeof value === 'number' ? Number(value) : undefined,
        [`max${fieldSuffix}`]: parentFieldValue === 'noCropping' ? undefined : maxValue,
      };
    case `max${fieldSuffix}`:
      return {
        [`min${fieldSuffix}`]: parentFieldValue === 'noCropping' ? undefined : minValue,
        [`max${fieldSuffix}`]: typeof value === 'number' ? Number(value) : undefined,
      };
    default:
      return 'noCropping';
  }
};
